/* eslint-disable react/display-name */
import React, { useState, useMemo, useCallback } from 'react';
import Header from '../../../../components/Table/Header';
import HeaderExtend from '../../../../components/Table/HeaderExtend';
import Cell from '../../../../components/Cell';
import Table from '../../../../components/Table';
import ContentTooltip from './ContentTooltip';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';
import styled from 'styled-components';
import StoreModal from '../StoreModal';

const PointOfSalesTableContainer = styled.div`
  .rt-th:first-child {
    position: sticky !important;
    left: 0 !important;
    background-color: white !important;
    z-index: 2 !important;
  }

  .rt-tbody {
    overflow: initial !important;
  }

  .rt-td:first-child {
    position: sticky !important;
    left: 0 !important;
    z-index: 2 !important;
    background-color: white !important;
  }

  .rt-tr.-odd .rt-td:first-child {
    background: rgba(247, 247, 247) !important;
  }

  .rt-tbody .rt-tr {
    cursor: ${(props) => (props.canClick ? 'pointer' : 'default')};
  }

  .rt-tbody .rt-tr:hover {
    background-color: ${(props) =>
      props.canClick ? 'rgba(226, 92, 50, 0.05)' : 'transparent'} !important;
  }

  .rt-tbody .rt-tr:hover .rt-td:first-child {
    color: ${(props) =>
      props.canClick ? props.theme.colors['terra-cotta'] : 'inherit'};
    font-weight: ${(props) => (props.canClick ? '600' : 'inherit')};
    text-decoration: ${(props) => (props.canClick ? 'underline' : 'none')};
    cursor: ${(props) => (props.canClick ? 'pointer' : 'default')};
  }

  .rt-tbody .rt-tr .rt-td:first-child {
    transition: all 0.2s ease;
  }
`;

const hasAtLeastOneRef = (data = [], property) => {
  return data
    ? data.map((item) => item[property]).reduce((base, acc) => base + acc, 0) >
        0
    : false;
};

const hasAtLeastOneRefBtg = (data = [], property) => {
  return (
    data
      .map((item) =>
        item[property]
          ? Object.values(item[property]).reduce((base, acc) => base + acc, 0)
          : 0
      )
      .reduce((base, acc) => base + acc, 0) > 0
  );
};

const sortByQuality = (a, b) => {
  const aMichelinStars = a.michelin_stars;
  const bMichelinStars = b.michelin_stars;
  const aWsAward = a.ws_award;
  const bWsAward = b.ws_award;

  // Check if michelin_stars has value (not null, undefined, empty string, or 0)
  const aHasMichelin =
    aMichelinStars !== null &&
    aMichelinStars !== undefined &&
    aMichelinStars !== '' &&
    aMichelinStars !== 0;
  const bHasMichelin =
    bMichelinStars !== null &&
    bMichelinStars !== undefined &&
    bMichelinStars !== '' &&
    bMichelinStars !== 0;

  // Check if ws_award has value (not null, undefined, or empty string)
  const aHasAward =
    aWsAward !== null && aWsAward !== undefined && aWsAward !== '';
  const bHasAward =
    bWsAward !== null && bWsAward !== undefined && bWsAward !== '';

  // Check if has any quality indicator at all
  const aHasAnyQuality = aHasMichelin || aHasAward;
  const bHasAnyQuality = bHasMichelin || bHasAward;

  // If both have no quality indicators, they're equal
  if (!aHasAnyQuality && !bHasAnyQuality) {
    return 0;
  }

  // Push entries with no quality to the end
  if (!aHasAnyQuality) return 1;
  if (!bHasAnyQuality) return -1;

  // Compare by michelin stars first (descending: higher stars come first)
  const michelinDiff =
    Number(bMichelinStars || 0) - Number(aMichelinStars || 0);
  if (michelinDiff !== 0) {
    return michelinDiff;
  }

  // If michelin stars are equal, sort by ws_award (with awards come before without)
  if (aHasAward && !bHasAward) {
    return -1;
  }
  if (!aHasAward && bHasAward) {
    return 1;
  }

  return 0;
};

const getColumns = ({
  data,
  onSortTable,
  isBranded,
  isContinuousMode,
  clientMarqueNames = [],
  rivalMarqueNames = [],
  hiddenColumns = [],
  brandsDeployed,
  rivalsDeployed,
  toggleBrandsDeployed,
  toggleRivalsDeployed,
  wineType
}) => {
  // The list is already scoped to wine_type, so the generic total column should match (dn_total mixes wine+spirit)
  const totalRefsAccessor = wineType === 'spirit' ? 'dn_spirits' : 'dn_wines';
  const hasBdxRefs = hasAtLeastOneRef(data, 'references_bdx');
  const hasChampRefs = hasAtLeastOneRef(data, 'references_champ');
  const hasBdxPrices = hasAtLeastOneRef(data, 'avg_bdx_price');
  const hasChampPrices = hasAtLeastOneRef(data, 'avg_champ_price');
  const hasBtgPrices = hasAtLeastOneRefBtg(data, 'client_wines_btg');
  const hasBtgRivalPrices = hasAtLeastOneRefBtg(data, 'rival_wines_btg');

  return [
    {
      id: 'name',
      name: I18n.t('restaurants&caves.pos.columns.pov'),
      accessor: (row) => row.pos,
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('pos')}
          styles={`justify-content:flex-start;padding-left:30px; .title {text-align: left}`}
          title={I18n.t('restaurants&caves.pos.columns.pov')}
        />
      ),
      Cell: (props) => {
        return (
          <Cell
            type="pellet"
            {...props}
            value={props.value}
            font="sfcompact_text"
            styles={`justify-content:flex-start;padding-left:30px;font-weight: 600;`}
            showRssOrangeIcon={props.original.is_flow_store}
          />
        );
      },
      sortMethod: (a, b) => {
        return String(a.value).localeCompare(String(b.value));
      },
      minWidth: 280
    },

    isContinuousMode && {
      id: 'checkValidityDate',
      name: I18n.t('restaurants&caves.pos.columns.checkValidityDate'),
      Header: () => (
        <Header
          sortable={true}
          // The id provided to onSortTable should be id or accessor if it's a string
          sort={() => onSortTable('checkValidityDate')}
          title={I18n.t('restaurants&caves.pos.columns.checkValidityDate')}
        />
      ),
      sortMethod: (a, b) => {
        return b.format('X') - a.format('X');
      },
      Cell: ({ value, ...props }) => (
        <Cell
          {...props}
          value={value ? value.format('DD/MM/YYYY') : '-'}
          styles={`border-right: 1px solid #e8e9eb`}
        />
      ),
      accessor: (row) =>
        row.continuous_mode && !!row.checks_validity_date
          ? row.checks_validity_date
          : null
    },

    isBranded && {
      name: I18n.t('restaurants&caves.pos.columns.myBrands'),
      accessor: 'brands_client',
      sortMethod: (a, b) =>
        a && b
          ? Number(Object.values(a).filter((v) => v > 0).length) -
            Number(Object.values(b).filter((v) => v > 0).length)
          : 0,
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('brands_client')}
          title={I18n.t('restaurants&caves.pos.columns.myBrands')}
        />
      ),
      Cell: (props) => (
        <Cell
          {...props}
          tooltip={
            props.value &&
            Object.values(props.value).filter((v) => v > 0).length > 0
              ? {
                  placement: 'top',
                  content: (
                    <ContentTooltip
                      values={Object.keys(props.value).filter(
                        (key) => props.value[key] > 0
                      )}
                      title={I18n.t('tooltipBrand')}
                    />
                  )
                }
              : undefined
          }
          value={
            props.value
              ? Object.values(props.value).filter((v) => v > 0).length
              : 0
          }
          font="sfcompact_text"
        />
      )
    },
    isBranded && {
      Header: () => (
        <HeaderExtend
          onClick={toggleBrandsDeployed}
          isRetracted={!brandsDeployed}
        />
      ),
      Cell: () => <Cell type="extend" />,
      maxWidth: 20
    },
    brandsDeployed &&
      clientMarqueNames.map((item) => ({
        Header: () => (
          <Header
            title={item}
            sortable={true}
            styles={`justify-content:flex-start;padding-left:30px; .title {text-align: left}`}
            sort={() => onSortTable('brands_client')}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            value={
              props.value && props.value[item] ? I18n.t('yes') : I18n.t('no')
            }
            font="sfcompact_text"
            styles={`justify-content:flex-start;padding-left:30px;`}
          />
        ),
        sortMethod: (a, b) =>
          a && b
            ? Number(Object.values(a).length) - Number(Object.values(b).length)
            : 0,
        accessor: 'brands_client',
        maxWidth: 120
      })),

    {
      name: I18n.t('restaurants&caves.pos.columns.nbSku'),
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('client_refs_by_wine')}
          title={I18n.t('restaurants&caves.pos.columns.nbSku')}
        />
      ),
      sortMethod: (a, b) =>
        Number(a ? Object.values(a).reduce((acc, base) => acc + base, 0) : 0) -
        Number(b ? Object.values(b).reduce((acc, base) => acc + base, 0) : 0),
      Cell: (props) => (
        <Cell
          {...props}
          value={
            props.value
              ? Object.values(props.value).reduce((acc, base) => acc + base, 0)
              : '-'
          }
          tooltip={
            props.value
              ? {
                  placement: 'top',
                  content: (
                    <ContentTooltip
                      title={I18n.t('tooltipWine')}
                      values={
                        // Filter out wines that have no references
                        Object.keys(props.value).filter(
                          (key) => props.value[key] > 0
                        )
                      }
                    />
                  )
                }
              : null
          }
          styles={`border-right: 1px solid #e8e9eb`}
        />
      ),

      accessor: 'client_refs_by_wine'
    },

    {
      name: I18n.t('restaurants&caves.pos.columns.evol'),
      Header: () => (
        <Header
          title={I18n.t('restaurants&caves.pos.columns.evol')}
          sortable={true}
          styles={`justify-content:flex-start;padding-left:30px; .title {text-align: left}`}
          sort={() => onSortTable('evolution')}
        />
      ),
      Cell: (props) => (
        <Cell
          {...props}
          font="sfcompact_text"
          styles={`justify-content:flex-start;padding-left:30px;`}
        />
      ),
      accessor: 'evolution',
      minWidth: 150
    },
    {
      name: I18n.t('restaurants&caves.pos.columns.nbRival'),
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('active_rivals')}
          title={I18n.t('restaurants&caves.pos.columns.nbRival')}
        />
      ),
      sortMethod: (a, b) => Number(a ? a.length : 0) - Number(b ? b.length : 0),
      Cell: (props) => (
        <Cell
          {...props}
          value={props.value.length}
          tooltip={
            props.value.length > 0
              ? {
                  placement: 'top',
                  content: (
                    <ContentTooltip
                      title={I18n.t('tooltipRivalWine')}
                      values={props.value}
                    />
                  )
                }
              : null
          }
          styles={`border-right: 1px solid #e8e9eb`}
        />
      ),
      accessor: 'active_rivals'
    },
    isBranded && {
      Header: () => (
        <HeaderExtend
          onClick={toggleRivalsDeployed}
          isRetracted={!rivalsDeployed}
        />
      ),
      Cell: () => <Cell type="extend" />,
      maxWidth: 20
    },
    rivalsDeployed &&
      rivalMarqueNames.map((item) => ({
        Header: () => (
          <Header
            title={item}
            sortable={true}
            styles={`justify-content:flex-start;padding-left:30px; .title {text-align: left}`}
            sort={() => onSortTable('rivals_wines_by_brand')}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            value={
              props.value && props.value[item] ? I18n.t('yes') : I18n.t('no')
            }
            font="sfcompact_text"
            styles={`justify-content:flex-start;padding-left:30px;`}
          />
        ),
        accessor: 'rivals_wines_by_brand',
        maxWidth: 120
      })),
    {
      name: I18n.t('restaurants&caves.pos.columns.city'),
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('city')}
          title={I18n.t('restaurants&caves.pos.columns.city')}
          styles={`justify-content:flex-start;padding-left:30px`}
        />
      ),
      Cell: (props) => (
        <Cell
          {...props}
          font="sfcompact_text"
          styles={`justify-content:flex-start;padding-left:30px;`}
        >
          {props.value}
        </Cell>
      ),

      accessor: 'city'
    },
    {
      name: I18n.t('restaurants&caves.pos.columns.contact'),
      accessor: 'contact',
      Header: () => (
        <Header title={I18n.t('restaurants&caves.pos.columns.contact')} />
      ),
      Cell: (props) => <Cell type="contact" {...props} font="sfcompact_text" />,

      minWidth: 150
    },
    {
      name: I18n.t('restaurants&caves.pos.columns.quality'),
      accessor: 'quality',
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('quality')}
          styles={`.title{font-size:12px};`}
          title={I18n.t('restaurants&caves.pos.columns.quality')}
        />
      ),
      Cell: (props) => {
        return (
          <Cell
            type="quality"
            {...props}
            styles={`justify-content:flex-start;padding-left:30px`}
            hideEmpty={
              props.value.stars === 0 ||
              props.value.stars === '' ||
              props.value.stars === null
            }
          />
        );
      },
      sortMethod: sortByQuality
    },
    hasBtgPrices && {
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('client_wines_btg')}
          title={I18n.t('restaurants&caves.pos.columns.nbRefWine')}
        />
      ),
      sortMethod: (a, b) =>
        Number(a ? Object.values(a).reduce((acc, base) => acc + base, 0) : 0) -
        Number(b ? Object.values(b).reduce((acc, base) => acc + base, 0) : 0),
      Cell: (props) => (
        <Cell
          {...props}
          styles={`border-right: 1px solid #e8e9eb`}
          value={
            props.value && props.value
              ? Number(
                  Object.values(props.value).reduce(
                    (acc, base) => acc + base,
                    0
                  )
                )
              : '-'
          }
          tooltip={
            props.value
              ? {
                  placement: 'top',
                  content: (
                    <ContentTooltip
                      title={I18n.t('tooltipWine')}
                      values={Object.keys(props.value)}
                    />
                  )
                }
              : null
          }
        />
      ),
      name: I18n.t('restaurants&caves.pos.columns.nbRefWine'),
      accessor: 'client_wines_btg'
    },
    hasBtgRivalPrices && {
      name: I18n.t('restaurants&caves.pos.columns.nbRefWineRival'),
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('rival_wines_btg')}
          title={I18n.t('restaurants&caves.pos.columns.nbRefWineRival')}
        />
      ),
      sortMethod: (a, b) =>
        Number(a ? Object.values(a).reduce((acc, base) => acc + base, 0) : 0) -
        Number(b ? Object.values(b).reduce((acc, base) => acc + base, 0) : 0),
      Cell: (props) => (
        <Cell
          {...props}
          styles={`border-right: 1px solid #e8e9eb`}
          value={
            props.value && props.value
              ? Number(
                  Object.values(props.value).reduce(
                    (acc, base) => acc + base,
                    0
                  )
                )
              : '-'
          }
          tooltip={
            props.value
              ? {
                  placement: 'top',
                  content: (
                    <ContentTooltip
                      title={I18n.t('tooltipRivalWine')}
                      values={Object.keys(props.value)}
                    />
                  )
                }
              : null
          }
        />
      ),

      accessor: 'rival_wines_btg'
    },
    hasBdxRefs && {
      name: I18n.t('restaurants&caves.pos.columns.nbBdx'),
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('references_bdx')}
          title={I18n.t('restaurants&caves.pos.columns.nbBdx')}
        />
      ),
      sortMethod: (a, b) => Number(a) - Number(b),
      Cell: (props) => <Cell {...props} />,

      accessor: 'references_bdx'
    },
    hasChampRefs && {
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('references_champ')}
          title={I18n.t('restaurants&caves.pos.columns.nbChamp')}
        />
      ),
      sortMethod: (a, b) => Number(a) - Number(b),
      Cell: (props) => <Cell {...props} />,
      name: I18n.t('restaurants&caves.pos.columns.nbChamp'),
      accessor: 'references_champ'
    },
    !hasChampRefs &&
      !hasBdxRefs && {
        Header: () => (
          <Header
            sortable={true}
            sort={() => onSortTable(totalRefsAccessor)}
            title={I18n.t('restaurants&caves.pos.columns.nbTotal')}
          />
        ),
        sortMethod: (a, b) => Number(a) - Number(b),
        Cell: (props) => <Cell {...props} />,
        name: I18n.t('restaurants&caves.pos.columns.nbTotal'),
        accessor: totalRefsAccessor
      },
    hasBdxPrices && {
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('avg_bdx_price')}
          title={I18n.t('restaurants&caves.pos.columns.avgPriceBdx')}
        />
      ),
      Cell: (props) => (
        <Cell
          {...props}
          value={
            props.value
              ? props.value.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: get(props, 'row._original.displayed_currency')
                    ? get(props, 'row._original.displayed_currency')
                    : 'EUR',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })
              : '-'
          }
        />
      ),
      name: I18n.t('restaurants&caves.pos.columns.avgPriceBdx'),
      accessor: 'avg_bdx_price'
    },
    hasChampPrices && {
      name: I18n.t('restaurants&caves.pos.columns.avgPriceChamp'),
      Header: () => (
        <Header
          sortable={true}
          sort={() => onSortTable('avg_champ_price')}
          title={I18n.t('restaurants&caves.pos.columns.avgPriceChamp')}
        />
      ),
      Cell: (props) => (
        <Cell
          {...props}
          value={
            props.value
              ? props.value.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: get(props, 'row._original.displayed_currency')
                    ? get(props, 'row._original.displayed_currency')
                    : 'EUR',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })
              : '-'
          }
        />
      ),
      accessor: 'avg_champ_price'
    },
    !hasChampPrices &&
      !hasBdxPrices && {
        name: I18n.t('restaurants&caves.pos.columns.medianPrice'),
        Header: () => (
          <Header
            sortable={true}
            sort={() => onSortTable('median_all_price')}
            title={I18n.t('restaurants&caves.pos.columns.medianPrice')}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            value={
              props.value
                ? props.value.toLocaleString('fr-FR', {
                    style: 'currency',
                    currency: get(props, 'row._original.displayed_currency')
                      ? get(props, 'row._original.displayed_currency')
                      : 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  })
                : '-'
            }
          />
        ),

        accessor: 'median_all_price'
      }
  ]
    .filter((item) => item && !hiddenColumns.includes(item.name))
    .flat();
};

const PointOfSalesTable = (props) => {
  const [brandsDeployed, setBrandsDeployed] = useState(false);
  const [rivalsDeployed, setRivalsDeployed] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const canAccessStorePopup = true;

  const toggleBrandsDeployed = useCallback(
    () => setBrandsDeployed((prev) => !prev),
    []
  );

  const toggleRivalsDeployed = useCallback(
    () => setRivalsDeployed((prev) => !prev),
    []
  );

  const {
    data,
    sortTable,
    isBranded,
    onSortTable,
    isContinuousMode,
    clientMarqueNames,
    rivalMarqueNames,
    hiddenColumns,
    showAll,
    isLatestCampaign,
    wineType
  } = props;

  const columns = useMemo(
    () =>
      getColumns({
        data,
        onSortTable,
        isBranded,
        isContinuousMode,
        clientMarqueNames,
        rivalMarqueNames,
        hiddenColumns,
        brandsDeployed,
        rivalsDeployed,
        toggleBrandsDeployed,
        toggleRivalsDeployed,
        wineType
      }),
    [
      data,
      onSortTable,
      isBranded,
      isContinuousMode,
      clientMarqueNames,
      rivalMarqueNames,
      hiddenColumns,
      brandsDeployed,
      rivalsDeployed,
      toggleBrandsDeployed,
      toggleRivalsDeployed,
      wineType
    ]
  );

  const dataSorted = useMemo(
    () =>
      [...data].sort((a, b) => {
        const sort =
          Number(
            a && a.client_refs_by_wine
              ? Object.values(a.client_refs_by_wine).reduce(
                  (acc, base) => acc + base,
                  0
                )
              : 0
          ) -
          Number(
            b && b.client_refs_by_wine
              ? Object.values(b.client_refs_by_wine).reduce(
                  (acc, base) => acc + base,
                  0
                )
              : 0
          );
        if (!sort) {
          if (isBranded) {
            return (
              Number(
                a && a.rivals_wines_by_brand
                  ? Object.values(a.rivals_wines_by_brand).length
                  : 0
              ) -
              Number(
                b && b.rivals_wines_by_brand
                  ? Object.values(b.rivals_wines_by_brand).length
                  : 0
              )
            );
          }
          return (
            Number(a && a.rival_wines ? Object.keys(a.rival_wines).length : 0) -
            Number(b && b.rival_wines ? Object.keys(b.rival_wines).length : 0)
          );
        }
        return sort;
      }),
    [data, isBranded]
  );

  const handleRowClick = useCallback(
    (state, rowInfo) => {
      const store = rowInfo && rowInfo.original;
      const storeId = store && store.store_id;

      if (canAccessStorePopup && storeId != null) {
        // Open the store modal
        setSelectedStoreId(storeId);
        setIsModalOpen(true);
      }
    },
    [canAccessStorePopup]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedStoreId(null);
  }, []);

  const getTrProps = useCallback(
    (state, rowInfo) => ({
      onClick: () => handleRowClick(state, rowInfo)
    }),
    [handleRowClick]
  );

  return (
    <>
      <PointOfSalesTableContainer canClick={canAccessStorePopup}>
        <Table
          data={dataSorted}
          columns={columns}
          sorted={sortTable}
          getTrProps={getTrProps}
          pageSize={showAll ? Math.max(dataSorted.length, 1) : 20}
        />
      </PointOfSalesTableContainer>

      <StoreModal
        storeId={selectedStoreId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        displayDocumentsTab={isLatestCampaign}
      />
    </>
  );
};

export default PointOfSalesTable;
